import React from 'react';
import { Section } from '../fx/AccentContext';
import PageIntro from './PageIntro';
import { MagneticCTA, SectionLabel, PADX } from '../home/ui';

/**
 * プライバシーポリシー・利用規約など、条文形式の法務ドキュメント共通レイアウト。
 * articles: [{ no, title, body: (string | { list: string[] })[] }]
 * contact: { intro, operator, representative, address, email, website }
 */
export default function LegalDocument({
  label,
  title,
  lead,
  articles,
  contact,
  establishedDate,
  revisedDate,
  accent = 'blue',
}) {
  return (
    <main className="bg-paper text-ink">
      <PageIntro
        label={label}
        title={title}
        titleClassName="mt-8 text-[9vw] font-black leading-[1.16] tracking-display-ja text-ink md:mt-10 md:text-[5.6vw] 2xl:text-[86px]"
        titlePer="line"
        lead={lead}
        accent={accent}
      />

      {/* 01 — ARTICLES */}
      <Section id="legal-articles" index={0} accent={accent} className="border-b border-line">
        <div className={`${PADX} py-20 md:py-28`}>
          <div className="grid grid-cols-12 lg:gap-x-14">
            <div className="col-span-12 lg:col-span-8 lg:col-start-3">
              <SectionLabel no="01">Articles</SectionLabel>

              <div className="mt-10 border-t border-line">
                {articles.map((article) => (
                  <article key={article.no} className="border-b border-line py-10">
                    <h2 className="text-[17px] font-bold tracking-display-ja text-ink md:text-[19px]">
                      <span className="text-live tnum mr-3">{article.no}</span>
                      {article.title}
                    </h2>
                    <div className="mt-5 space-y-4 text-[14px] leading-[1.9] text-gray-700 md:text-[15px]">
                      {article.body.map((block, i) =>
                        typeof block === 'string' ? (
                          <p key={i}>{block}</p>
                        ) : (
                          <ol key={i} className="space-y-2">
                            {block.list.map((item, j) => (
                              <li key={j} className="flex gap-3">
                                <span className="shrink-0 text-gray-400 tnum">{j + 1}.</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ol>
                        )
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 02 — CONTACT */}
      <Section id="legal-contact" index={1} accent={accent}>
        <div className={`${PADX} py-20 md:py-28`}>
          <div className="grid grid-cols-12 lg:gap-x-14">
            <div className="col-span-12 lg:col-span-8 lg:col-start-3">
              <SectionLabel no="02">Contact</SectionLabel>
              <div className="mt-10 border border-line p-8 md:p-10">
                <p className="text-[13px] leading-relaxed text-gray-600">{contact.intro}</p>
                <dl className="mt-6 space-y-3 border-t border-line pt-6 text-[14px] leading-relaxed text-ink">
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
                    <dt className="w-24 shrink-0 text-[12px] uppercase tracking-label text-gray-500">
                      運営者
                    </dt>
                    <dd>{contact.operator}</dd>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
                    <dt className="w-24 shrink-0 text-[12px] uppercase tracking-label text-gray-500">
                      代表者
                    </dt>
                    <dd>{contact.representative}</dd>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
                    <dt className="w-24 shrink-0 text-[12px] uppercase tracking-label text-gray-500">
                      所在地
                    </dt>
                    <dd>{contact.address}</dd>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
                    <dt className="w-24 shrink-0 text-[12px] uppercase tracking-label text-gray-500">
                      メール
                    </dt>
                    <dd>
                      <a href={`mailto:${contact.email}`} className="hover:text-live">
                        {contact.email}
                      </a>
                    </dd>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
                    <dt className="w-24 shrink-0 text-[12px] uppercase tracking-label text-gray-500">
                      ウェブサイト
                    </dt>
                    <dd>
                      <a
                        href={contact.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-live"
                      >
                        {contact.website}
                      </a>
                    </dd>
                  </div>
                </dl>
              </div>
              <p className="mt-6 text-[12px] text-gray-500">制定日：{establishedDate}</p>
              {revisedDate && (
                <p className="mt-1 text-[12px] text-gray-500">最終改定日：{revisedDate}</p>
              )}

              <div className="mt-16">
                <MagneticCTA to="/contact" variant="outline">
                  お問い合わせへ戻る
                </MagneticCTA>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </main>
  );
}
